import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { api } from '@/api'
import type { ModelFamilyInfo } from '@/api/types'
import {
  DEFAULT_FAMILY_ID,
  FALLBACK_FAMILIES,
  familiesOf,
  familyById,
  familyIdFromLegacy,
  slugForFamily,
} from '@/config/modelFamilies'

/*
  v1 → v2：视频档改为「只下发已实测的」（2026-09-30，后端 config.ONLY_VERIFIED_VIDEO）。

  **必须换 key**：v1 的缓存里躺着 16 条视频模型（含 12 条从没跑通过的）。缓存只在
  「后端没连上」时生效，不换 key 的话那一刻会把这些已删掉的选项原样放回下拉 ——
  现象跟没改一样，而且极难排查（后端明明是干净的）。

  v2 → v3：视频档**新增两条**（2026-10-01）——`youkou-wan-3-0`（阿里云 Wan 3.0）
  与 `google-omni`（改走速创 API）。同一条道理反过来用：v2 的缓存里没有这两条，
  后端没连上时用户会发现「刚加的两个模型又不见了」。
*/
const CACHE_KEY = 'hw_model_families_v3'

type ModelsContextValue = {
  /** 全部模型（含不可用项，一般不用直接渲染它） */
  all: ModelFamilyInfo[]
  /** 某类型下真实可选的模型。`text` 是文案类（LLM）、`audio` 是语音合成 */
  list: (kind: 'image' | 'video' | 'audio' | 'text') => ModelFamilyInfo[]
  /** 清单是否已就绪（缓存命中或拉取完成） */
  ready: boolean
  /** 拉取失败时的说明；此时用兜底清单 */
  error: string | null
  /** 来源：后端实时 / 本地缓存 / 内置兜底 */
  source: 'remote' | 'cache' | 'fallback'
  refresh: () => void
  /** 表单里存的可能是旧 slug，统一转成 family id */
  normalize: (value: string, kind: 'image' | 'video' | 'audio' | 'text') => string
  /** 模型 + 有没有参考图 → 实际提交 slug */
  toSlug: (familyId: string, kind: 'image' | 'video', hasRef: boolean) => string
  /**
   * skill 加载器是否在**后端环境**打开（`HW_SKILLS_ENABLE`）。
   *
   * 关着时文案页**不渲染**那个开关 —— 后端没开时点它必然 400
   * （SKILLS_DISABLED），放一个必然报错的死控件比不放更费解。
   * 拉取失败一律当 false：默认关是安全方向，宁可不显示也不要显示错的。
   */
  skillsEnabled: boolean
  /** 技能索引条数，用于开关旁的「可从 N 个技能里取文档」 */
  skillCount: number
}

const ModelsContext = createContext<ModelsContextValue | null>(null)

function readCache(): ModelFamilyInfo[] | null {
  try {
    // 顺手清掉上一代缓存：它的视频档是「全量 16 条」，留着只会让人以为改动没生效
    localStorage.removeItem('hw_model_families_v1')
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ModelFamilyInfo[]
    return Array.isArray(parsed) && parsed.length ? parsed : null
  } catch {
    return null
  }
}

function writeCache(list: ModelFamilyInfo[]) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(list))
  } catch {
    /* 配额满 / 隐私模式，忽略 */
  }
}

/**
 * 模型清单。
 *
 * 只从后端拉，不在前端维护模型列表——前端一旦硬编码，就会和真实能力脱节
 * （上一版硬编码了 31 个参考站 slug，其中 20 个我们根本跑不了）。
 * 拉取失败时用内置兜底清单，保证离线 / mock 模式也能用。
 */
export function ModelsProvider({ children }: { children: ReactNode }) {
  const cached = useRef<ModelFamilyInfo[] | null>(readCache())
  const [all, setAll] = useState<ModelFamilyInfo[]>(cached.current ?? FALLBACK_FAMILIES)
  const [source, setSource] = useState<'remote' | 'cache' | 'fallback'>(
    cached.current ? 'cache' : 'fallback',
  )
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [skills, setSkills] = useState<{ enabled: boolean; count: number }>({
    enabled: false,
    count: 0,
  })
  const inflight = useRef(false)

  const load = useCallback(async () => {
    if (inflight.current) return
    inflight.current = true
    try {
      // 与模型清单**并发**取：两者互相不依赖，串行等于让文案页多等一个 RTT。
      const [list, skillIndex] = await Promise.all([
        api.listModels(),
        // 技能索引纯属附加信息，拉不到不该影响模型清单能否用 —— 单独兜住
        api.listSkills().catch(() => null),
      ])
      setAll(list)
      setSource('remote')
      setError(null)
      writeCache(list)
      if (skillIndex) setSkills({ enabled: skillIndex.enabled, count: skillIndex.count })
    } catch (e) {
      // 有缓存就继续用缓存，别因为一次拉取失败把已经能用的界面搞挂
      setError(e instanceof Error ? e.message : '模型清单拉取失败')
      setSource(cached.current ? 'cache' : 'fallback')
    } finally {
      setReady(true)
      inflight.current = false
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const value = useMemo<ModelsContextValue>(
    () => ({
      all,
      list: (kind) => familiesOf(all, kind),
      ready,
      error,
      source,
      refresh: () => void load(),
      normalize: (v, kind) => {
        const hit = familyIdFromLegacy(all, v)
        if (hit) {
          const f = familyById(all, hit)
          // 旧值指向了另一种类型（比如草稿存的是图片模型但当前是视频页）→ 用默认
          if (f && f.kind === kind) return hit
        }
        return familiesOf(all, kind)[0]?.id ?? DEFAULT_FAMILY_ID[kind]
      },
      toSlug: (familyId, kind, hasRef) => {
        const f = familyById(all, familyId) ?? familiesOf(all, kind)[0]
        if (!f) return ''
        return slugForFamily(f, hasRef)
      },
      skillsEnabled: skills.enabled,
      skillCount: skills.count,
    }),
    [all, ready, error, source, load, skills],
  )

  return <ModelsContext.Provider value={value}>{children}</ModelsContext.Provider>
}

export function useModels() {
  const ctx = useContext(ModelsContext)
  if (!ctx) throw new Error('useModels 必须在 ModelsProvider 内使用')
  return ctx
}
